import React, { useState } from 'react';
import {
        Box,
        Card,
        Typography,
        Button,
        TextField,
        InputAdornment,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableRow,
        Grid,
        useTheme,
} from '@mui/material';
import {
        Search,
        Add,
        Inventory,
} from '@mui/icons-material';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { simpleApi, InventoryTransaction } from '../../services/simpleApi';

interface Receipt {
	id: string;
	purchase_order_id: string;
	received_at: string;
	received_by: string;
	items: any[];
}

const ReceivingPage: React.FC = () => {
	const theme = useTheme();
	const [searchQuery, setSearchQuery] = useState('');
	const [receipts, setReceipts] = useState<Receipt[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const fetchItems = async () => {
		try {
			setLoading(true);
			// Get receiving transactions (type = 'receipt' or 'receiving')
			const response = await simpleApi.inventory.getTransactions({ transaction_type: 'receipt' });
			const items: Receipt[] = response.items.map((t: InventoryTransaction) => ({
				id: t.id,
				purchase_order_id: t.reference_id || 'N/A',
				received_at: t.created_at,
				received_by: t.created_by || 'System',
				items: [{ product_id: t.product_id, quantity: t.quantity }],
			}));
			setReceipts(items);
			setError(null);
		} catch (err: any) {
			setError(err.message || 'Failed to load receipts');
		} finally {
			setLoading(false);
		}
	};

	React.useEffect(() => {
		fetchItems();
	}, []);

	const clearSuccess = () => setSuccess(null);        const filteredReceipts = (receipts || []).filter(receipt =>
                receipt.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                receipt.purchase_order_id.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return (
                <Box>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                                Receiving
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                                Process inbound shipments and receipts
                                        </Typography>
                                </Box>
                                <Button variant="contained" startIcon={<Add />}>
                                        Process Receipt
                                </Button>
                        </Box>

                        {error && <ErrorAlert error={error} onRetry={fetchItems} />}

                        {/* Search */}
                        <Card sx={{ mb: 3, p: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={6}>
                                                <TextField
                                                        fullWidth
                                                        placeholder="Search receipts or PO numbers..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        InputProps={{
                                                                startAdornment: (
                                                                        <InputAdornment position="start">
                                                                                <Search />
                                                                        </InputAdornment>
                                                                ),
                                                        }}
                                                />
                                        </Grid>
                                </Grid>
                        </Card>

                        {/* Receipts Table */}
                        <Card>
                                {loading ? (
                                        <LoadingState message="Loading receipts..." />
                                ) : filteredReceipts.length === 0 ? (
                                        <EmptyState
                                                title="No receipts found"
                                                description="Process your first inbound shipment"
                                                actionLabel="Process Receipt"
                                                icon={<Inventory sx={{ fontSize: 64, color: 'text.disabled' }} />}
                                        />
                                ) : (
                                        <TableContainer>
                                                <Table>
                                                        <TableHead>
                                                                <TableRow>
                                                                        <TableCell>Receipt ID</TableCell>
                                                                        <TableCell>Received Date</TableCell>
                                                                        <TableCell>PO Number</TableCell>
                                                                        <TableCell>Received By</TableCell>
                                                                        <TableCell>Items Count</TableCell>
                                                                        <TableCell align="right">Actions</TableCell>
                                                                </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                                {filteredReceipts.map((receipt) => (
                                                                        <TableRow key={receipt.id} hover>
                                                                                <TableCell>
                                                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                                {receipt.id.substring(0, 8)}...
                                                                                        </Typography>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                        {new Date(receipt.received_at).toLocaleString()}
                                                                                </TableCell>
                                                                                <TableCell>{receipt.purchase_order_id}</TableCell>
                                                                                <TableCell>{receipt.received_by}</TableCell>
                                                                                <TableCell>{receipt.items.length}</TableCell>
                                                                                <TableCell align="right">
                                                                                        <Button size="small">View Details</Button>
                                                                                </TableCell>
                                                                        </TableRow>
                                                                ))}
                                                        </TableBody>
                                                </Table>
                                        </TableContainer>
                                )}
                        </Card>

                        <SuccessSnackbar
                                open={!!success}
                                message={success || ''}
                                onClose={clearSuccess}
                        />
                </Box>
        );
};

export default ReceivingPage;
